import { Button } from "../../design-v2";
import { AUTH_PROVIDER_LABELS } from "../providerCapabilities";

export const LAST_USED_AUTH_PROVIDER_KEY = "lz_last_auth_provider";

function GoogleMark() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path fill="#4285F4" d="M21.6 12.2c0-.7-.1-1.4-.2-2H12v3.9h5.4a4.6 4.6 0 0 1-2 3v2.5h3.3c1.9-1.8 2.9-4.4 2.9-7.4Z" />
      <path fill="#34A853" d="M12 22c2.7 0 5-.9 6.7-2.4l-3.3-2.5c-.9.6-2.1 1-3.4 1a5.9 5.9 0 0 1-5.5-4.1H3.1v2.6A10 10 0 0 0 12 22Z" />
      <path fill="#FBBC05" d="M6.5 14a6 6 0 0 1 0-3.9V7.5H3.1a10 10 0 0 0 0 9.1L6.5 14Z" />
      <path fill="#EA4335" d="M12 6a5.4 5.4 0 0 1 3.8 1.5l2.9-2.8A9.8 9.8 0 0 0 3.1 7.5l3.4 2.6A5.9 5.9 0 0 1 12 6Z" />
    </svg>
  );
}

function AppleMark() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path fill="currentColor" d="M16.7 12.8c0-2.3 1.9-3.4 2-3.5a4.3 4.3 0 0 0-3.4-1.8c-1.4-.1-2.8.9-3.5.9-.8 0-1.9-.9-3.1-.9-1.6 0-3.1.9-4 2.4-1.7 3-.4 7.3 1.2 9.7.8 1.2 1.8 2.5 3.1 2.4 1.2-.1 1.7-.8 3.2-.8 1.4 0 1.9.8 3.2.8 1.3 0 2.2-1.2 3-2.4a10.6 10.6 0 0 0 1.4-2.9 4.1 4.1 0 0 1-2.1-3.9ZM14.4 6c.7-.9 1.2-2.1 1.1-3.3-1.1.1-2.4.7-3.2 1.6-.7.8-1.3 2-1.1 3.2 1.2.1 2.4-.6 3.2-1.5Z" />
    </svg>
  );
}

function EmailMark() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M4 6.5h16v11H4z" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
      <path d="m5 7.5 7 5.5 7-5.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// The provider marks are decorative and provider-agnostic, so Settings reuses
// them rather than keeping a second copy of the same artwork.
export function ProviderMark({ providerId }) {
  if (providerId === "google") {
    return <GoogleMark />;
  }
  if (providerId === "apple") {
    return <AppleMark />;
  }
  return <EmailMark />;
}

export function readLastUsedAuthProvider(storage = window.localStorage) {
  try {
    const provider = storage?.getItem(LAST_USED_AUTH_PROVIDER_KEY);
    return typeof provider === "string" && provider ? provider : null;
  } catch {
    return null;
  }
}

export function rememberLastUsedAuthProvider(
  providerId,
  storage = window.localStorage
) {
  try {
    if (typeof providerId === "string" && providerId) {
      storage?.setItem(LAST_USED_AUTH_PROVIDER_KEY, providerId);
    }
  } catch {
    // The hint is display-only; unavailable storage must never block auth.
  }
}

export function clearLastUsedAuthProvider(
  providerId,
  storage = window.localStorage
) {
  try {
    if (storage?.getItem(LAST_USED_AUTH_PROVIDER_KEY) === providerId) {
      storage.removeItem(LAST_USED_AUTH_PROVIDER_KEY);
    }
  } catch {
    // The hint is display-only; unavailable storage must never block unlink.
  }
}

export default function ProviderButton({
  providerId,
  isLastUsed = false,
  loadingLabel,
  ...buttonProps
}) {
  const providerName = AUTH_PROVIDER_LABELS[providerId] ||
    `${String(providerId || "provider").charAt(0).toUpperCase()}${String(providerId || "provider").slice(1)}`;
  return (
    <Button
      {...buttonProps}
      variant="secondary"
      size="lg"
      loadingLabel={loadingLabel ?? `Connecting with ${providerName}...`}
      className={`lz-auth-provider${isLastUsed ? " lz-auth-provider--last-used" : ""}${buttonProps.className ? ` ${buttonProps.className}` : ""}`}
      data-provider-id={providerId}
    >
      <span className="lz-auth-provider__mark"><ProviderMark providerId={providerId} /></span>
      <span className="lz-auth-provider__text">Continue with {providerName}</span>
      <span className="lz-auth-provider__status">
        {isLastUsed ? <span className="lz-auth-provider__last-used">LAST USED</span> : null}
      </span>
    </Button>
  );
}
